import { reactive } from 'vue'
import axios from 'axios'
import { API_ENDPOINTS } from '@/config/api.endpoints'
import type {
  AvalaraManagementCompany,
  CompanyNexusDetail,
  CompanyOverview,
  InferredGoodsType,
  NexusEntry,
  NexusStatus,
  NexusSyncSelection,
  NexusSyncResult,
  NexusSyncFailure,
  NexusTreeNode,
  NexusTreeNodeData,
  SetDefaultTaxCodeRequest,
  SetDefaultTaxCodeResult,
} from '@/types/avalaraManagementCompany'
import type {
  AvalaraCompanySyncBusyDetails,
  AvalaraCompanySyncResult,
  AvalaraCompanySyncRunStatus,
  AvalaraCompanySyncSource,
  AvalaraCompanySyncStatus,
} from '@/types/avalaraSync'
import {
  COMPANY_SYNC_ERROR_CODES,
  NO_COMPANY_SYNC_STATUS,
} from '@/types/avalaraSync'
import type {
  AvalaraCompanyCreateRequest,
  AvalaraCompanyCreateResult,
  EuParametersStatus,
} from '@/types/avalaraCompanyCreate'
import type { EuRegistrationConfig } from '@/types/euRegistrationConfig'
import { EU_REGISTRATION_PARAMETER_NAMES } from '@/types/avalaraEuRegistration'
import type {
  CompanyEuRegistration,
  EuRegistrationParameterName,
} from '@/types/avalaraEuRegistration'
import type {
  BulkAlignState,
  BulkAlignError,
  BulkAlignSubmitRequest,
} from '@/types/bulkAlign'
import type { PagedResponse } from '@/types/pagedResponse'
import type { AuthState } from '@/types/authState'
import { ApiError } from '@/util/ApiError'
import { normalizeError } from '@/util/errorNormalizer'

const AVALARA_MANAGEMENT = API_ENDPOINTS.AVALARA

export enum API_STATES {
  INITIAL = 'INITIAL',
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  COMPLETE = 'COMPLETE',
}

// ---- Wire-format interfaces ----
// All Avalara management backend types use PascalCase (explicit @JsonProperty annotations).
// List row: scalar counts per template. Detail: hierarchical tree with per-template status per node.

interface NexusEntryWire {
  Country: string
  Region: string
  JurisTypeId: string
  JurisCode: string
  JurisName: string | null
  TaxTypeGroup: string
}

interface AvalaraManagementCompanyWire {
  Id: number
  CompanyCode: string
  Name: string
  DigitalMissingCount: number | null
  DigitalExtraCount: number | null
  PhysicalMissingCount: number | null
  PhysicalExtraCount: number | null
  InferredGoodsType?: InferredGoodsType
  ProviderUrl?: string
}

interface NexusTreeNodeDataWire {
  Country: string
  Region: string
  JurisTypeId: string
  JurisCode: string
  JurisName: string | null
  TaxTypeGroup: string
  DigitalStatus: NexusStatus | null
  PhysicalStatus: NexusStatus | null
  EndedOnCompany?: boolean
}

interface NexusTreeNodeWire {
  Key: string
  Data: NexusTreeNodeDataWire
  Children: NexusTreeNodeWire[]
}

interface CompanyNexusDetailWire {
  Id: number
  CompanyCode: string
  Name: string
  NexusTree: NexusTreeNodeWire[]
  InferredGoodsType?: InferredGoodsType
  ProviderUrl?: string
}

interface NexusSummaryWire {
  TotalNexusEntries: number
  DigitalMissingCount: number | null
  DigitalExtraCount: number | null
  PhysicalMissingCount: number | null
  PhysicalExtraCount: number | null
}

interface ItemsSummaryWire {
  TotalItems: number
  UncategorizedItems: number
  DriftedItems: number
  AutoAssignedItems: number
}

interface CompanyOverviewWire {
  Id: number
  CompanyCode: string
  Name: string
  AccountId: number
  InferredGoodsType?: InferredGoodsType
  ProviderUrl?: string
  DefaultTaxCode?: string | null
  Nexus: NexusSummaryWire
  Items: ItemsSummaryWire
}

interface SetDefaultTaxCodeResultWire {
  CompanyId: number
  DefaultTaxCode: string | null
}

// Both lists are optional, and their absence is meaningful rather than cosmetic
// Request-mode truth table:
//   Additions absent, Removals absent  → sync-from-template, delta from the template
//   Additions absent, Removals non-empty → 400 INVALID_REQUEST
//   Additions []                        → explicit mode that adds nothing
//   Additions non-empty                 → explicit mode, the Nexus tab's path
interface NexusSyncRequestWire {
  TemplateType: 'DIGITAL' | 'PHYSICAL'
  Additions?: NexusEntryWire[]
  Removals?: NexusEntryWire[]
}

interface NexusSyncFailureWire {
  Country: string
  Region: string
  JurisTypeId: string
  JurisCode: string
  TaxTypeGroup: string
  Operation: 'ADD' | 'REMOVE'
  ErrorMessage: string
}

interface NexusSyncResultWire {
  Added: NexusEntryWire[]
  Removed: NexusEntryWire[]
  Failed: NexusSyncFailureWire[]
}

// The company-create 201 body is UpperCamelCase while its request body stays
// camelCase. Do not "tidy" these keys — the casing is pinned by a
// unit test because an unmapped body reads companyId as undefined and the
// post-create navigation lands on /avalara-companies/undefined.
interface AvalaraCompanyCreateResultWire {
  CompanyId: number
  CompanyCode: string
  ParametersApplied?: boolean
  /** Optional so a response from an older backend still maps; absent is treated
   *  as NOT_REQUESTED, which is the pre-existing meaning of ParametersApplied: false. */
  EuParametersStatus?: EuParametersStatus
}

// ---- Mappers ----

function mapEntryFromWire(wire: NexusEntryWire): NexusEntry {
  return {
    country: wire.Country,
    region: wire.Region,
    jurisTypeId: wire.JurisTypeId,
    jurisCode: wire.JurisCode,
    jurisName: wire.JurisName ?? null,
    taxTypeGroup: wire.TaxTypeGroup,
  }
}

function mapEntryToWire(entry: NexusEntry): NexusEntryWire {
  return {
    Country: entry.country,
    Region: entry.region,
    JurisTypeId: entry.jurisTypeId,
    JurisCode: entry.jurisCode,
    JurisName: entry.jurisName,
    TaxTypeGroup: entry.taxTypeGroup,
  }
}

function mapCompanyFromWire(wire: AvalaraManagementCompanyWire): AvalaraManagementCompany {
  return {
    id: wire.Id,
    companyCode: wire.CompanyCode,
    name: wire.Name,
    digitalMissingCount: wire.DigitalMissingCount,
    digitalExtraCount: wire.DigitalExtraCount,
    physicalMissingCount: wire.PhysicalMissingCount,
    physicalExtraCount: wire.PhysicalExtraCount,
    inferredGoodsType: wire.InferredGoodsType,
    providerUrl: wire.ProviderUrl,
  }
}

function mapTreeNodeDataFromWire(wire: NexusTreeNodeDataWire): NexusTreeNodeData {
  return {
    country: wire.Country,
    region: wire.Region,
    jurisTypeId: wire.JurisTypeId,
    jurisCode: wire.JurisCode,
    jurisName: wire.JurisName ?? null,
    taxTypeGroup: wire.TaxTypeGroup,
    digitalStatus: wire.DigitalStatus,
    physicalStatus: wire.PhysicalStatus,
    endedOnCompany: wire.EndedOnCompany ?? false,
  }
}

function mapTreeNodeFromWire(wire: NexusTreeNodeWire): NexusTreeNode {
  return {
    key: wire.Key,
    data: mapTreeNodeDataFromWire(wire.Data),
    children: (wire.Children ?? []).map(mapTreeNodeFromWire),
  }
}

function mapCompanyNexusDetailFromWire(wire: CompanyNexusDetailWire): CompanyNexusDetail {
  return {
    id: wire.Id,
    companyCode: wire.CompanyCode,
    name: wire.Name,
    nexusTree: (wire.NexusTree ?? []).map(mapTreeNodeFromWire),
    inferredGoodsType: wire.InferredGoodsType,
    providerUrl: wire.ProviderUrl,
  }
}

function mapCompanyOverviewFromWire(wire: CompanyOverviewWire): CompanyOverview {
  return {
    id: wire.Id,
    companyCode: wire.CompanyCode,
    name: wire.Name,
    accountId: wire.AccountId,
    inferredGoodsType: wire.InferredGoodsType,
    providerUrl: wire.ProviderUrl,
    defaultTaxCode: wire.DefaultTaxCode,
    nexus: {
      totalNexusEntries: wire.Nexus.TotalNexusEntries,
      digitalMissingCount: wire.Nexus.DigitalMissingCount,
      digitalExtraCount: wire.Nexus.DigitalExtraCount,
      physicalMissingCount: wire.Nexus.PhysicalMissingCount,
      physicalExtraCount: wire.Nexus.PhysicalExtraCount,
    },
    items: {
      totalItems: wire.Items.TotalItems,
      uncategorizedItems: wire.Items.UncategorizedItems,
      driftedItems: wire.Items.DriftedItems,
      autoAssignedItems: wire.Items.AutoAssignedItems,
    },
  }
}

function mapSetDefaultTaxCodeResultFromWire(
  wire: SetDefaultTaxCodeResultWire,
): SetDefaultTaxCodeResult {
  return {
    companyId: wire.CompanyId,
    defaultTaxCode: wire.DefaultTaxCode,
  }
}

function mapCompanyCreateResultFromWire(
  wire: AvalaraCompanyCreateResultWire,
): AvalaraCompanyCreateResult {
  return {
    companyId: wire.CompanyId,
    companyCode: wire.CompanyCode,
    parametersApplied: wire.ParametersApplied ?? false,
    euParametersStatus: wire.EuParametersStatus ?? 'NOT_REQUESTED',
  }
}

/**
 * With a selection, emits explicit mode: both keys are always present, empty
 * arrays included. Without one, both keys are omitted from the object so
 * JSON.stringify drops them and the backend selects sync-from-template. Setting
 * them to undefined would not be equivalent for a reader of this code, and
 * `Additions: []` would mean "add nothing" — see NexusSyncRequestWire.
 */
function mapSyncRequestToWire(
  templateType: 'digital' | 'physical',
  selection?: NexusSyncSelection,
): NexusSyncRequestWire {
  return {
    TemplateType: templateType === 'digital' ? 'DIGITAL' : 'PHYSICAL',
    ...(selection
      ? {
        Additions: selection.additions.map(mapEntryToWire),
        Removals: selection.removals.map(mapEntryToWire),
      }
      : {}),
  }
}

function mapSyncResultFromWire(wire: NexusSyncResultWire): NexusSyncResult {
  const mapFailure = (f: NexusSyncFailureWire): NexusSyncFailure => ({
    country: f.Country,
    region: f.Region,
    jurisTypeId: f.JurisTypeId,
    jurisCode: f.JurisCode,
    taxTypeGroup: f.TaxTypeGroup,
    operation: f.Operation,
    errorMessage: f.ErrorMessage,
  })
  return {
    added: (wire.Added ?? []).map(mapEntryFromWire),
    removed: (wire.Removed ?? []).map(mapEntryFromWire),
    failed: (wire.Failed ?? []).map(mapFailure),
  }
}

// ---- Bulk Align wire interfaces ----

// BE uses @JsonInclude(NON_NULL) on BulkAlignErrorResource — any field may be omitted
// from the wire. CompanyId is consistently populated in practice (errors are per-company),
// but companyCode can be absent when the DB lookup fails during error enrichment.
interface BulkAlignErrorWire {
  CompanyId?: number
  CompanyCode?: string
  ErrorMessage?: string
}

interface BulkAlignStateWire {
  IsRunning?: boolean
  RequestedBy?: string
  StartedAt?: string
  CompletedAt?: string
  TotalCount?: number
  ProcessedCount?: number
  SucceededCount?: number
  FailedCount?: number
  Errors?: BulkAlignErrorWire[]
}

interface BulkAlignSubmitRequestWire {
  Mode: 'FULL_DELTA' | 'ADD_MISSING_ONLY'
  TemplateType: 'DIGITAL' | 'PHYSICAL'
  CompanyIds: number[]
}

// ---- Bulk Align mappers ----

function mapBulkAlignErrorFromWire(wire: BulkAlignErrorWire): BulkAlignError {
  return {
    companyId: wire.CompanyId,
    companyCode: wire.CompanyCode,
    errorMessage: wire.ErrorMessage,
  }
}

function mapBulkAlignStateFromWire(wire: BulkAlignStateWire): BulkAlignState {
  return {
    isRunning: wire.IsRunning,
    requestedBy: wire.RequestedBy,
    startedAt: wire.StartedAt,
    completedAt: wire.CompletedAt,
    totalCount: wire.TotalCount,
    processedCount: wire.ProcessedCount,
    succeededCount: wire.SucceededCount,
    failedCount: wire.FailedCount,
    errors: (wire.Errors ?? []).map(mapBulkAlignErrorFromWire),
  }
}

// ---- Company sync wire interfaces + mapper ----

interface AvalaraNexusFailureWire {
  CompanyId?: number
  CompanyCode?: string
  Error?: string
}

interface AvalaraCompanySyncResultWire {
  Added?: number
  Updated?: number
  Deleted?: number
  NexusCompaniesSucceeded?: number
  NexusCompaniesFailed?: number
  NexusFailures?: AvalaraNexusFailureWire[]
  SyncedAt?: string
}

// The backend omits null keys (NON_NULL), and answers `{}` when the pod has had no run.
interface AvalaraCompanySyncStatusWire {
  Status?: string
  Source?: string
  RequestedBy?: string
  StartedAt?: string
  CompletedAt?: string
  Result?: AvalaraCompanySyncResultWire
  Error?: { Code?: string; Message?: string }
}

const COMPANY_SYNC_RUN_STATUSES: readonly string[] = [
  'RUNNING',
  'COMPLETED',
  'FAILED',
]
const COMPANY_SYNC_SOURCES: readonly string[] = ['ADHOC', 'SCHEDULED']

function mapCompanySyncSourceFromWire(
  source: string | undefined,
): AvalaraCompanySyncSource | undefined {
  return source !== undefined && COMPANY_SYNC_SOURCES.includes(source)
    ? (source as AvalaraCompanySyncSource)
    : undefined
}

function mapCompanySyncResultFromWire(
  wire: AvalaraCompanySyncResultWire,
): AvalaraCompanySyncResult {
  return {
    added: wire.Added ?? 0,
    updated: wire.Updated ?? 0,
    deleted: wire.Deleted ?? 0,
    nexusCompaniesSucceeded: wire.NexusCompaniesSucceeded ?? 0,
    nexusCompaniesFailed: wire.NexusCompaniesFailed ?? 0,
    nexusFailures: (wire.NexusFailures ?? []).map(f => ({
      companyId: f.CompanyId ?? 0,
      companyCode: f.CompanyCode ?? '',
      error: f.Error ?? '',
    })),
    syncedAt: wire.SyncedAt ?? '',
  }
}

function mapCompanySyncStatusFromWire(
  wire: AvalaraCompanySyncStatusWire,
): AvalaraCompanySyncStatus {
  const known =
    wire.Status !== undefined && COMPANY_SYNC_RUN_STATUSES.includes(wire.Status)
  return {
    status: known ? (wire.Status as AvalaraCompanySyncRunStatus) : null,
    source: mapCompanySyncSourceFromWire(wire.Source),
    requestedBy: wire.RequestedBy,
    startedAt: wire.StartedAt,
    completedAt: wire.CompletedAt,
    result: wire.Result ? mapCompanySyncResultFromWire(wire.Result) : undefined,
    error: wire.Error
      ? { code: wire.Error.Code, message: wire.Error.Message }
      : undefined,
  }
}

/**
 * Reads the running sync's details from a 409 COMPANY_SYNC_BUSY error.
 * Returns null for any other error, or when the details carry no StartedAt.
 */
export function companySyncBusyDetails(
  err: unknown,
): AvalaraCompanySyncBusyDetails | null {
  if (!(err instanceof ApiError) || err.code !== COMPANY_SYNC_ERROR_CODES.BUSY)
    return null
  const body = err.originalResponse as
    | {
        Details?: { RequestedBy?: string; StartedAt?: unknown; Source?: string }
      }
    | undefined
  const details = body?.Details
  if (!details || typeof details.StartedAt !== 'string') return null
  return {
    requestedBy: details.RequestedBy,
    startedAt: details.StartedAt,
    source: mapCompanySyncSourceFromWire(details.Source),
  }
}

// ---- EU Registration Config wire interface + mapper ----

interface EuRegistrationConfigWire {
  Ioss?: string
  OssNonUnion?: string
  OssUnion?: string
}

function mapEuRegistrationConfigFromWire(
  wire: EuRegistrationConfigWire,
): EuRegistrationConfig {
  return {
    ioss: wire.Ioss,
    ossNonUnion: wire.OssNonUnion,
    ossUnion: wire.OssUnion,
  }
}

// ---- Company EU registration wire interface + mapper ----

interface EuRegistrationParameterWire {
  Name: string
  IsSet: boolean
  Value?: string
}

interface CompanyEuRegistrationWire {
  Parameters: EuRegistrationParameterWire[]
}

// A body that cannot say for certain whether a parameter is set must not render as "Not set".
function mapCompanyEuRegistrationFromWire(
  wire: CompanyEuRegistrationWire | null,
): CompanyEuRegistration {
  const entries = new Map<string, EuRegistrationParameterWire>()
  for (const entry of wire?.Parameters ?? []) entries.set(entry.Name, entry)
  const result = {} as CompanyEuRegistration
  for (const name of EU_REGISTRATION_PARAMETER_NAMES) {
    const entry = entries.get(name)
    if (!entry) throw new Error(`Unexpected response: ${name} is missing`)
    if (typeof entry.IsSet !== 'boolean') {
      throw new Error(`Unexpected response: ${name} has no IsSet`)
    }
    if (!entry.IsSet) {
      result[name] = { isSet: false, value: null }
      continue
    }
    if (typeof entry.Value !== 'string') {
      throw new Error(`Unexpected response: ${name} is set but has no Value`)
    }
    result[name] = { isSet: true, value: entry.Value }
  }
  return result
}

function mapBulkAlignSubmitToWire(req: BulkAlignSubmitRequest): BulkAlignSubmitRequestWire {
  return {
    Mode: req.mode,
    TemplateType: req.templateType,
    CompanyIds: req.companyIds,
  }
}

function nexusSyncUrl(companyId: number): string {
  return `${AVALARA_MANAGEMENT.MANAGEMENT_COMPANIES}/${companyId}/nexus/sync`
}

// ---- API params type ----

export interface GetCompaniesParams {
  page?: number
  size?: number
  name?: string
  id?: number
  companyCode?: string
  sort?: string
}

// ---- Service interface ----

interface AvalaraManagementApi {
  state: API_STATES
  error: ApiError | null
  getCompanies: (
    authState: AuthState,
    params?: GetCompaniesParams,
  ) => Promise<PagedResponse<AvalaraManagementCompany>>
  getCompanyNexusDetail: (
    authState: AuthState,
    companyId: number,
  ) => Promise<CompanyNexusDetail>
  getCompanyOverview: (
    authState: AuthState,
    companyId: number,
  ) => Promise<CompanyOverview>
  /** Sets or clears a company's default Avalara tax code. A null/blank
   *  taxCode clears the default. */
  setDefaultTaxCode: (
    authState: AuthState,
    companyId: number,
    taxCode: string | null,
  ) => Promise<SetDefaultTaxCodeResult>
  /** Explicit mode: sends the operator's chosen additions and removals, empty
   *  arrays included. Used by the Nexus tab's Match actions. */
  syncNexus: (
    authState: AuthState,
    companyId: number,
    templateType: 'digital' | 'physical',
    selection: NexusSyncSelection,
  ) => Promise<NexusSyncResult>
  /** Step 2 of the two-step create flow. Sync-from-template mode:
   *  the backend computes the delta against the template and deletes nothing.
   *  Idempotent, so a failed attempt is retried by calling this again with the
   *  same arguments. A non-empty `failed` list arrives as a resolved 200. */
  syncNexusFromTemplate: (
    authState: AuthState,
    companyId: number,
    templateType: 'digital' | 'physical',
  ) => Promise<NexusSyncResult>
  /** Starts the sync in the background. Resolves on 202 with no body. */
  syncCompanies: (authState: AuthState) => Promise<void>
  /** Run status of the pod that answers. `{ status: null }` when it had no run. */
  getCompanySyncStatus: (
    authState: AuthState,
  ) => Promise<AvalaraCompanySyncStatus>
  createCompany: (
    authState: AuthState,
    payload: AvalaraCompanyCreateRequest,
  ) => Promise<AvalaraCompanyCreateResult>
  submitBulkAlign: (
    authState: AuthState,
    req: BulkAlignSubmitRequest,
  ) => Promise<void>
  getBulkAlignState: (authState: AuthState) => Promise<BulkAlignState>
  getEuRegistrationConfig: (authState: AuthState) => Promise<EuRegistrationConfig>
  /** Live read of the 4 EU parameters. Rejects when the body cannot say for each
   *  parameter whether it is set. Does not touch the shared `state`/`error`. */
  getEuRegistration: (authState: AuthState, companyId: number) => Promise<CompanyEuRegistration>
  /** Adds or resets one parameter. `value` is sent only for IsIntermediaryAppointed. */
  putEuRegistrationParameter: (
    authState: AuthState,
    companyId: number,
    name: EuRegistrationParameterName,
    value?: boolean,
  ) => Promise<void>
  deleteEuRegistrationParameter: (
    authState: AuthState,
    companyId: number,
    name: EuRegistrationParameterName,
  ) => Promise<void>
}

// available as global state
export const avalaraManagementApi: AvalaraManagementApi = reactive({
  state: API_STATES.INITIAL,
  error: null,
  getCompanies(
    authState: AuthState,
    params?: GetCompaniesParams,
  ): Promise<PagedResponse<AvalaraManagementCompany>> {
    const token = authState.user?.accessToken
    avalaraManagementApi.state = API_STATES.LOADING
    avalaraManagementApi.error = null

    // Build params object — only include defined values
    const queryParams: Record<string, string | number> = {
      page: params?.page ?? 0,
      size: params?.size ?? 20,
    }
    if (params?.name !== undefined && params.name !== '') queryParams.name = params.name
    if (params?.id !== undefined) queryParams.id = params.id
    if (params?.companyCode !== undefined && params.companyCode !== '') queryParams.companyCode = params.companyCode
    if (params?.sort !== undefined && params.sort !== '') queryParams.sort = params.sort

    return axios
      .get<PagedResponse<AvalaraManagementCompanyWire>>(
        AVALARA_MANAGEMENT.MANAGEMENT_COMPANIES,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: queryParams,
        },
      )
      .then(({ data }) => {
        if (Array.isArray(data.Content)) {
          avalaraManagementApi.state = API_STATES.COMPLETE
          return {
            ...data,
            Content: data.Content.map(mapCompanyFromWire),
          }
        }
        throw Error('Unexpected response')
      })
      .catch(err => {
        avalaraManagementApi.state = API_STATES.ERROR
        avalaraManagementApi.error = normalizeError(err)
        throw avalaraManagementApi.error
      })
  },
  getCompanyNexusDetail(
    authState: AuthState,
    companyId: number,
  ): Promise<CompanyNexusDetail> {
    const token = authState.user?.accessToken
    const url = `${AVALARA_MANAGEMENT.MANAGEMENT_COMPANIES}/${companyId}/nexus`
    return axios
      .get<CompanyNexusDetailWire>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => mapCompanyNexusDetailFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  getCompanyOverview(
    authState: AuthState,
    companyId: number,
  ): Promise<CompanyOverview> {
    const token = authState.user?.accessToken
    const url = AVALARA_MANAGEMENT.OVERVIEW_FOR_COMPANY(companyId)
    return axios
      .get<CompanyOverviewWire>(url, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => mapCompanyOverviewFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  setDefaultTaxCode(
    authState: AuthState,
    companyId: number,
    taxCode: string | null,
  ): Promise<SetDefaultTaxCodeResult> {
    const token = authState.user?.accessToken
    const url = AVALARA_MANAGEMENT.SET_DEFAULT_TAX_CODE(companyId)
    const body: SetDefaultTaxCodeRequest = { TaxCode: taxCode }
    return axios
      .put<SetDefaultTaxCodeResultWire>(url, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then(({ data }) => mapSetDefaultTaxCodeResultFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  syncNexus(
    authState: AuthState,
    companyId: number,
    templateType: 'digital' | 'physical',
    selection: NexusSyncSelection,
  ): Promise<NexusSyncResult> {
    const token = authState.user?.accessToken
    const body = mapSyncRequestToWire(templateType, selection)
    return axios
      .post<NexusSyncResultWire>(nexusSyncUrl(companyId), body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then(({ data }) => mapSyncResultFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  syncNexusFromTemplate(
    authState: AuthState,
    companyId: number,
    templateType: 'digital' | 'physical',
  ): Promise<NexusSyncResult> {
    const token = authState.user?.accessToken
    const body = mapSyncRequestToWire(templateType)
    return axios
      .post<NexusSyncResultWire>(nexusSyncUrl(companyId), body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then(({ data }) => mapSyncResultFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  syncCompanies(authState: AuthState): Promise<void> {
    const token = authState.user?.accessToken
    return axios
      .post(AVALARA_MANAGEMENT.MANAGEMENT_COMPANY_SYNC, undefined, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => undefined)
      .catch(err => {
        throw normalizeError(err)
      })
  },
  getCompanySyncStatus(
    authState: AuthState,
  ): Promise<AvalaraCompanySyncStatus> {
    const token = authState.user?.accessToken
    return axios
      .get<AvalaraCompanySyncStatusWire>(
        AVALARA_MANAGEMENT.MANAGEMENT_COMPANY_SYNC,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      .then(({ data }) =>
        data !== null && typeof data === 'object'
          ? mapCompanySyncStatusFromWire(data)
          : NO_COMPANY_SYNC_STATUS,
      )
      .catch(err => {
        throw normalizeError(err)
      })
  },
  /** Step 1 of the two-step create flow. Creates the company with
   *  no nexus; the caller then applies the template nexus with
   *  syncNexusFromTemplate using the returned companyId. The 201 body is
   *  UpperCamelCase and is mapped, not passed through. */
  createCompany(
    authState: AuthState,
    payload: AvalaraCompanyCreateRequest,
  ): Promise<AvalaraCompanyCreateResult> {
    const token = authState.user?.accessToken
    return axios
      .post<AvalaraCompanyCreateResultWire>(
        AVALARA_MANAGEMENT.MANAGEMENT_COMPANIES,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      )
      .then(({ data }) => mapCompanyCreateResultFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  submitBulkAlign(authState: AuthState, req: BulkAlignSubmitRequest): Promise<void> {
    const token = authState.user?.accessToken
    const body = mapBulkAlignSubmitToWire(req)
    return axios
      .post(AVALARA_MANAGEMENT.BULK_ALIGN, body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .then(() => undefined)
      .catch(err => {
        throw normalizeError(err)
      })
  },
  getBulkAlignState(authState: AuthState): Promise<BulkAlignState> {
    const token = authState.user?.accessToken
    return axios
      .get<BulkAlignStateWire>(AVALARA_MANAGEMENT.BULK_ALIGN, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => mapBulkAlignStateFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  getEuRegistrationConfig(authState: AuthState): Promise<EuRegistrationConfig> {
    const token = authState.user?.accessToken
    return axios
      .get<EuRegistrationConfigWire>(AVALARA_MANAGEMENT.EU_REGISTRATION_CONFIG, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => mapEuRegistrationConfigFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  getEuRegistration(authState: AuthState, companyId: number): Promise<CompanyEuRegistration> {
    const token = authState.user?.accessToken
    return axios
      .get<CompanyEuRegistrationWire>(AVALARA_MANAGEMENT.EU_REGISTRATION_FOR_COMPANY(companyId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(({ data }) => mapCompanyEuRegistrationFromWire(data))
      .catch(err => {
        throw normalizeError(err)
      })
  },
  putEuRegistrationParameter(
    authState: AuthState,
    companyId: number,
    name: EuRegistrationParameterName,
    value?: boolean,
  ): Promise<void> {
    const token = authState.user?.accessToken
    const url = AVALARA_MANAGEMENT.EU_REGISTRATION_PARAMETER(companyId, name)
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    const body = value === undefined ? undefined : { Value: value }
    if (body) headers['Content-Type'] = 'application/json'
    return axios
      .put(url, body, { headers })
      .then(() => undefined)
      .catch(err => {
        throw normalizeError(err)
      })
  },
  deleteEuRegistrationParameter(
    authState: AuthState,
    companyId: number,
    name: EuRegistrationParameterName,
  ): Promise<void> {
    const token = authState.user?.accessToken
    return axios
      .delete(AVALARA_MANAGEMENT.EU_REGISTRATION_PARAMETER(companyId, name), {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => undefined)
      .catch(err => {
        throw normalizeError(err)
      })
  },
})
